import { 
  doc, 
  setDoc, 
  getDoc, 
  collection,
  query,
  where,
  getDocs,
  orderBy,
  limit,
  Timestamp
} from 'firebase/firestore';
import { 
  ref, 
  uploadBytes, 
  getDownloadURL, 
  deleteObject 
} from 'firebase/storage';
import { db, storage } from '../firebase';
import { v4 as uuidv4 } from 'uuid';

import { formatDateTimeDisplay } from './formatDateTime';

export interface ChecklistItem {
  id: string;
  text: string;
  checked: boolean;
  required: boolean;
  photo?: string;
  photoFile?: File;
  status?: 'green' | 'yellow' | 'red' | '';
  comment?: string;
  photoStoragePath?: string; // Storage path for the uploaded photo
}

export interface ChecklistCategory {
  id: string;
  name: string;
  items: ChecklistItem[];
  color: string;
}

export interface PreDepartureChecklistData {
  customId: string;
  plateNumber: string;
  companyId: string;
  companyName: string;
  completed_by: string;
  completed_at: Timestamp;
  updated_by: string;
  updated_at: Timestamp;
  created_at: Timestamp;
  categories: ChecklistCategory[];
  totalRequired: number;
  completedRequired: number;
  isComplete: boolean;
  status: 'ongoing' | 'completed' | 'attached'; // Status flow: ongoing → completed → attached (to waybill)
  overallStatus?: 'pass' | 'fail'; // Final assessment
  inspectionSummary?: string; // Inspector's summary
  signatureDataURL?: string; // Digital signature
  location?: string;
  notes?: string;
  wayBillNumber?: string; // Set when status becomes 'attached'
  attachedAt?: Timestamp; // Timestamp when attached to waybill
}

export interface ChecklistTemplate {
  id: string;
  name: string;
  companyId?: string; // Optional - if not set, it's the default template
  categories: ChecklistCategory[];
  created_at: Timestamp;
  updated_at: Timestamp;
  isActive: boolean;
}

export interface Vehicle {
  id: string;
  plateNo: string;
  lastRegistration: Timestamp;
  registrationToExpire: Timestamp;
  image?: string; // Storage URL for vehicle image
  active: boolean;
  companyId?: string; // Optional - for filtering by company
}

/**
 * Generate a custom ID for the checklist
 * Format: plateNo_date_uuid
 */
export const generateChecklistId = (plateNumber: string): string => {
  const date = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
  const uuid = uuidv4().split('-')[0]; // First part of UUID for brevity
  const cleanPlateNumber = plateNumber.replace(/[^a-zA-Z0-9]/g, ''); // Remove special characters
  return `${cleanPlateNumber}_${date}_${uuid}`;
};

/**
 * Upload a photo to Firebase Storage
 * Path: /checklist_photos/{plateNo}/pending/{categoryId}/{lineItemId}.jpg
 */
export const uploadChecklistPhoto = async (
  customId: string,
  companyId: string,
  plateNo: string,
  categoryId: string,
  itemId: string,
  photoFile: File
): Promise<{ downloadURL: string; storagePath: string }> => {
  try {
    // Clean plate number for file path
    const cleanPlateNo = plateNo.replace(/[^a-zA-Z0-9]/g, '');
    
    // Create storage path with new structure
    const storagePath = `checklist_photos/${companyId}/${cleanPlateNo}/${customId}/${categoryId}/${itemId}.jpg`;
    const storageRef = ref(storage, storagePath);
    
    // Upload file
    const snapshot = await uploadBytes(storageRef, photoFile);
    
    // Get download URL
    const downloadURL = await getDownloadURL(snapshot.ref);
    
    return { downloadURL, storagePath };
  } catch (error) {
    console.error('Error uploading photo:', error);
    throw new Error('Failed to upload photo');
  }
};

/**
 * Delete a photo from Firebase Storage
 */
export const deleteChecklistPhoto = async (storagePath: string): Promise<void> => {
  try {
    const storageRef = ref(storage, storagePath);
    await deleteObject(storageRef);
  } catch (error) {
    console.error('Error deleting photo:', error);
    // Don't throw error if file doesn't exist
    if (error instanceof Error && !error.message.includes('object-not-found')) {
      throw error;
    }
  }
};

/**
 * Generate document ID for the checklist
 * Format: {plateNo}_{companyId}_{uuid} (unique ID for each checklist)
 */
export const generateChecklistDocumentId = (plateNumber: string, companyId?: string): string => {
  const cleanPlateNumber = plateNumber.replace(/[^a-zA-Z0-9]/g, '');
  const uuid = uuidv4().split('-')[0]; // First part of UUID for brevity
  
  if (companyId) {
    return `${cleanPlateNumber}_${companyId}_${uuid}`;
  }
  return `${cleanPlateNumber}_${uuid}`;
};

/**
 * Upload photos for a specific category (for real-time uploads) with detailed tracking
 */
export const uploadCategoryPhotos = async (
  customId: string,
  companyId: string,
  plateNo: string,
  category: ChecklistCategory
): Promise<{
  updatedCategory: ChecklistCategory;
  uploadedCount: number;
  failedCount: number;
  totalCount: number;
  failedItems: string[];
}> => {
  const updatedCategory = { ...category };
  let uploadedCount = 0;
  let failedCount = 0;
  let totalCount = 0;
  const failedItems: string[] = [];
  
  for (let itemIndex = 0; itemIndex < updatedCategory.items.length; itemIndex++) {
    const item = updatedCategory.items[itemIndex];
    
    if (item.photoFile) {
      totalCount++;
      try {
        // If there's an existing photo, delete it first
        if (item.photoStoragePath) {
          try {
            await deleteChecklistPhoto(item.photoStoragePath);
          } catch (deleteError) {
            console.warn('Warning: Could not delete existing photo:', deleteError);
            // Continue with upload even if delete fails
          }
        }
        
        // Upload new photo with new structure
        const { downloadURL, storagePath } = await uploadChecklistPhoto(
          customId,
          companyId,
          plateNo,
          category.id,
          item.id,
          item.photoFile
        );
        
        // Update item with photo URL and storage path
        updatedCategory.items[itemIndex] = {
          ...item,
          photo: downloadURL,
          photoStoragePath: storagePath,
          photoFile: undefined // Remove file reference after successful upload
        };
        
        uploadedCount++;
      } catch (error) {
        console.error(`Error uploading photo for item ${item.text}:`, error);
        failedCount++;
        failedItems.push(item.text);
        // Keep photoFile for retry - don't remove it on failure
      }
    }
  }
  
  return {
    updatedCategory,
    uploadedCount,
    failedCount,
    totalCount,
    failedItems
  };
};

/**
 * Upload photos selectively - only upload new photos (those with photoFile)
 * and preserve existing photos
 */
export const uploadCategoryPhotosSelectively = async (
  customId: string,
  companyId: string,
  plateNo: string,
  categories: ChecklistCategory[]
): Promise<ChecklistCategory[]> => {
  const updatedCategories = [...categories];
  
  for (let categoryIndex = 0; categoryIndex < updatedCategories.length; categoryIndex++) {
    const category = updatedCategories[categoryIndex];
    
    for (let itemIndex = 0; itemIndex < category.items.length; itemIndex++) {
      const item = category.items[itemIndex];
      
      // Only process items that have new photo files
      if (item.photoFile) {
        try {
          // Upload new photo (always use pending path)
          const { downloadURL, storagePath } = await uploadChecklistPhoto(
            customId,
            companyId,
            plateNo,
            category.id,
            item.id,
            item.photoFile
          );
          
          // Update item with photo URL and storage path
          updatedCategories[categoryIndex].items[itemIndex] = {
            ...item,
            photo: downloadURL,
            photoStoragePath: storagePath,
            photoFile: undefined // Remove file reference after upload
          };
        } catch (error) {
          console.error(`Error uploading photo for item ${item.text}:`, error);
          // Keep photoFile for retry on error
        }
      }
    }
  }
  
  return updatedCategories;
};


/**
 * Upload all photos for a checklist and update categories with download URLs
 */
export const uploadChecklistPhotos = async (
  customId: string,
  companyId: string,
  plateNo: string,
  categories: ChecklistCategory[]
): Promise<ChecklistCategory[]> => {
  const updatedCategories = [...categories];
  
  for (let categoryIndex = 0; categoryIndex < updatedCategories.length; categoryIndex++) {
    const category = updatedCategories[categoryIndex];
    
    for (let itemIndex = 0; itemIndex < category.items.length; itemIndex++) {
      const item = category.items[itemIndex];
      
      if (item.photoFile) {
        try {
          // If there's an existing photo, delete it first
          if (item.photoStoragePath) {
            await deleteChecklistPhoto(item.photoStoragePath);
          }
          
          // Upload new photo (always use pending path since we keep one record)
          const { downloadURL, storagePath } = await uploadChecklistPhoto(
            customId,
            companyId,
            plateNo,
            category.id,
            item.id,
            item.photoFile
          );
          
          // Update item with photo URL and storage path
          updatedCategories[categoryIndex].items[itemIndex] = {
            ...item,
            photo: downloadURL,
            photoStoragePath: storagePath,
            photoFile: undefined // Remove file reference after upload
          };
        } catch (error) {
          console.error(`Error uploading photo for item ${item.text}:`, error);
          // Continue with other photos even if one fails
        }
      }
    }
  }
  
  return updatedCategories;
};

/**
 * Save or update pending checklist to Firestore
 * Business Logic:
 * - If there's an ongoing/completed checklist for this vehicle → update it
 * - If the existing checklist is attached → create a new one with UUID
 * - Any save/update changes status back to 'ongoing'
 */
export const savePendingChecklistToFirestore = async (
  checklistData: any,
  plateNumber: string,
  companyId: string
): Promise<any> => {
  try {
    // First, check if there's an existing ongoing or completed checklist for this vehicle
    const existingChecklist = await getPendingChecklistFromFirestore(plateNumber, companyId);
    
    let docId: string;
    let existingData = null;
    
    if (existingChecklist && existingChecklist.status !== 'attached') {
      // Use existing checklist ID if it's ongoing or completed
      docId = existingChecklist.customId;
      existingData = existingChecklist;
    } else {
      // Create new checklist (either no existing or existing is attached)
      docId = generateChecklistDocumentId(plateNumber, companyId);
    }
    
    const docRef = doc(db, 'preDepartureChecklist', docId);
    
    let categoriesWithPhotos = [...checklistData.categories];
    
    if (existingData) {
      // Preserve existing photos and only upload new ones
      categoriesWithPhotos = checklistData.categories.map((category: ChecklistCategory) => {
        const existingCategory = existingData.categories?.find(cat => cat.id === category.id);
        
        const updatedItems = category.items.map((item: ChecklistItem) => {
          const existingItem = existingCategory?.items.find(existingItm => existingItm.id === item.id);
          
          if (existingItem && existingItem.photo && existingItem.photoStoragePath && !item.photoFile) {
            // Keep existing photo
            return {
              ...item,
              photo: existingItem.photo,
              photoStoragePath: existingItem.photoStoragePath
            };
          }
          
          return item;
        });
        
        return {
          ...category,
          items: updatedItems
        };
      });
    }
    
    // Only upload photos for items that have photoFile (new photos)
    const categoriesWithNewPhotos = await uploadCategoryPhotosSelectively(
      docId,
      companyId, 
      plateNumber, 
      categoriesWithPhotos
    );
    
    // Ensure timestamps are proper Firestore Timestamps
    const currentTimestamp = Timestamp.now();
    
    // Merge with existing data to preserve other fields
    const baseData = existingData || {};
    
    // Clean data to remove undefined values and set status to ongoing
    const cleanedData = removeUndefinedFields({
      ...baseData,
      ...checklistData,
      customId: docId,
      plateNumber: plateNumber,
      companyId: companyId,
      status: 'ongoing', // Always ongoing when saving progress
      isComplete: false,
      categories: categoriesWithNewPhotos,
      created_at: (baseData as any).created_at || currentTimestamp,
      updated_at: currentTimestamp
    });
    
    // Save to Firestore using merge to preserve other fields
    await setDoc(docRef, cleanedData, { merge: true });
    
    return cleanedData;
  } catch (error) {
    console.error('Error saving pending checklist to Firestore:', error);
    throw error;
  }
};

/**
 * Get pending checklist from Firestore
 * Returns the ongoing or completed checklist for this vehicle (not attached ones)
 */
export const getPendingChecklistFromFirestore = async (plateNumber: string, companyId: string): Promise<PreDepartureChecklistData | null> => {
  try {
    if (!plateNumber || !companyId) {
      console.error('Invalid parameters for getting pending checklist');
      return null;
    }

    // Query for checklists for this vehicle that are ongoing or completed (not attached)
    const q = query(
      collection(db, 'preDepartureChecklist'),
      where('plateNumber', '==', plateNumber),
      where('companyId', '==', companyId),
      where('status', 'in', ['ongoing']),
      orderBy('created_at', 'desc'),
      limit(1)
    );
    
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      const doc = querySnapshot.docs[0];
      const data = doc.data() as PreDepartureChecklistData;
      
      return {
        ...data,
        customId: doc.id
      };
    }

    return null;
  } catch (error) {
    console.error('Error getting pending checklist from Firestore:', error);
    throw new Error('Failed to retrieve pending checklist');
  }
};

/**
 * Delete pending checklist and cleanup all associated photos
 */
export const deletePendingChecklistAndPhotos = async (checklistId: string): Promise<void> => {
  try {
    const docRef = doc(db, 'preDepartureChecklist', checklistId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      
      // Collect all photo storage paths for cleanup
      if (data.categories) {
        const photosPaths = data.categories.flatMap((category: ChecklistCategory) =>
          category.items
            .filter(item => item.photoStoragePath)
            .map(item => item.photoStoragePath!)
        );
        
        // Delete all photos
        for (const photoPath of photosPaths) {
          try {
            await deleteChecklistPhoto(photoPath);
          } catch (error) {
            console.warn('Warning: Could not delete photo:', photoPath, error);
          }
        }
      }
      
      // Delete the document
      await setDoc(docRef, {}, { merge: false }); // This effectively deletes the document
    }
  } catch (error) {
    console.error('Error deleting pending checklist and photos:', error);
    throw new Error('Failed to delete pending checklist');
  }
};

/**
 * Convert ongoing checklist to completed status
 * Updates the same document from status='ongoing' to status='completed'
 */
export const convertPendingToCompleted = async (
  plateNumber: string,
  companyId: string,
  finalChecklistData: any
): Promise<string> => {
  try {
    // Get the existing ongoing/completed checklist for this vehicle
    const existingChecklist = await getPendingChecklistFromFirestore(plateNumber, companyId);
    
    if (!existingChecklist) {
      throw new Error('No ongoing checklist found for this vehicle');
    }
    
    // Check if checklist is attached to waybill - if so, cannot modify
    if (existingChecklist.status === 'attached') {
      throw new Error('Cannot modify checklist that is already attached to a waybill');
    }

    const docRef = doc(db, 'preDepartureChecklist', existingChecklist.customId);

    // Handle photo removals - delete photos that were removed by user
    const updatedCategories = await Promise.all(
      finalChecklistData.categories.map(async (category: ChecklistCategory) => {
        const updatedItems = await Promise.all(
          category.items.map(async (item: ChecklistItem) => {
            // Find corresponding item in existing checklist
            const existingCategory = existingChecklist.categories.find(cat => cat.id === category.id);
            const existingItem = existingCategory?.items.find(existingItm => existingItm.id === item.id);
            
            // If user removed the photo (it existed before but not now), delete it
            if (existingItem && existingItem.photoStoragePath && !item.photo && !item.photoFile) {
              try {
                await deleteChecklistPhoto(existingItem.photoStoragePath);
              } catch (error) {
                console.warn('Warning: Could not delete removed photo:', error);
              }
              return {
                ...item,
                photo: undefined,
                photoStoragePath: undefined
              };
            }
            
            // Keep existing photo data if no changes
            if (existingItem && existingItem.photo && !item.photoFile) {
              return {
                ...item,
                photo: existingItem.photo,
                photoStoragePath: existingItem.photoStoragePath
              };
            }
            
            return item;
          })
        );
        
        return {
          ...category,
          items: updatedItems
        };
      })
    );
    
    // Upload any new photos
    const categoriesWithNewPhotos = await uploadCategoryPhotosSelectively(existingChecklist.customId, companyId, plateNumber, updatedCategories);
    
    // Update the existing document to completed status
    const updatedData = {
      ...finalChecklistData,
      customId: existingChecklist.customId,
      plateNumber: plateNumber,
      companyId: companyId,
      status: 'completed', // Change from 'ongoing' to 'completed'
      categories: categoriesWithNewPhotos,
      completed_at: Timestamp.now(), // Set final completion timestamp
      updated_at: Timestamp.now()
    };
    
    // Update the same document
    await setDoc(docRef, removeUndefinedFields(updatedData));
    
    return existingChecklist.customId;
  } catch (error) {
    console.error('Error converting ongoing to completed checklist:', error);
    throw error;
  }
};

/**
 * Attach a completed checklist to a waybill
 * Changes status from 'completed' to 'attached' and sets waybill number
 */
export const attachChecklistToWaybill = async (
  checklistId: string,
  wayBillNumber: string,
  attachedBy: string
): Promise<void> => {
  try {
    const docRef = doc(db, 'preDepartureChecklist', checklistId);
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      throw new Error('Checklist not found');
    }
    
    const checklistData = docSnap.data() as PreDepartureChecklistData;
    
    // Can only attach completed checklists
    if (checklistData.status !== 'completed') {
      throw new Error('Can only attach completed checklists to waybills');
    }
    
    // Update to attached status
    const updateData = {
      status: 'attached',
      wayBillNumber: wayBillNumber,
      attachedAt: Timestamp.now(),
      attached_by: attachedBy,
      updated_at: Timestamp.now()
    };
    
    await setDoc(docRef, updateData, { merge: true });
  } catch (error) {
    console.error('Error attaching checklist to waybill:', error);
    throw error;
  }
};

/**
 * Check if a vehicle can start a new checklist
 * Returns true if no ongoing/completed checklist exists or if existing checklist is attached
 */
export const canStartNewChecklist = async (plateNumber: string, companyId: string): Promise<boolean> => {
  try {
    // Check if there's an ongoing or completed checklist
    const existingChecklist = await getPendingChecklistFromFirestore(plateNumber, companyId);
    
    if (!existingChecklist) {
      return true; // No ongoing/completed checklist found
    }
    
    // If checklist is ongoing or completed, cannot start new one
    // If checklist is attached, can start new one
    return existingChecklist.status === 'attached';
  } catch (error) {
    console.error('Error checking if can start new checklist:', error);
    return false;
  }
};

/**
 * Get checklist from Firestore
 */
export const getChecklistFromFirestore = async (customId: string): Promise<PreDepartureChecklistData | null> => {
  try {
    const docRef = doc(db, 'preDepartureChecklist', customId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return docSnap.data() as PreDepartureChecklistData;
    } else {
      return null;
    }
  } catch (error) {
    console.error('Error getting checklist from Firestore:', error);
    throw new Error('Failed to retrieve checklist');
  }
};

/**
 * Update an existing checklist
 */
export const updateChecklistInFirestore = async (
  customId: string,
  companyId: string,
  updateData: Partial<PreDepartureChecklistData>,
  plateNumber?: string
): Promise<void> => {
  try {
    // If categories are being updated and contain new photos, upload them
    if (updateData.categories && plateNumber) {
      const categoriesWithPhotos = await uploadChecklistPhotos(customId, companyId, plateNumber, updateData.categories);
      updateData.categories = categoriesWithPhotos;
    }
    
    // Add updated timestamp
    const dataWithTimestamp = {
      ...updateData,
      updated_at: Timestamp.now()
    };
    
    // Clean undefined values
    const cleanedData = removeUndefinedFields(dataWithTimestamp);
    
    const docRef = doc(db, 'preDepartureChecklist', customId);
    await setDoc(docRef, cleanedData, { merge: true });
  } catch (error) {
    console.error('Error updating checklist in Firestore:', error);
    throw new Error('Failed to update checklist');
  }
};

/**
 * Calculate checklist completion stats
 */
export const calculateChecklistStats = (categories: ChecklistCategory[]) => {
  const totalRequired = categories.reduce((sum, category) => 
    sum + category.items.filter(item => item.required).length, 0
  );
  console.log({totalRequired},{categories})
  const completedRequired = categories.reduce((sum, category) => 
    sum + category.items.filter(item => item.required && item.status !== undefined && item.status !== '').length, 0
  );
  
  const totalItems = categories.reduce((sum, category) => sum + category.items.length, 0);
  
  const completedItems = categories.reduce((sum, category) => 
    sum + category.items.filter(item => item.status !== undefined && item.status !== '').length, 0
  );
  
  // Check if all items with "red" status have photos
  const itemsWithRedStatus: ChecklistItem[] = categories.reduce((items, category) => 
    [...items, ...category.items.filter(item => item.status === 'red')], [] as ChecklistItem[]
  );
  
  const redItemsWithPhotos = itemsWithRedStatus.filter(item => item.photo || item.photoFile || item.photoStoragePath);
  const allRedItemsHavePhotos = itemsWithRedStatus.length === 0 || redItemsWithPhotos.length === itemsWithRedStatus.length;
  
  return {
    totalRequired,
    completedRequired,
    totalItems,
    completedItems,
    isComplete: completedRequired === totalRequired && allRedItemsHavePhotos
  };
};

/**
 * Remove undefined values from an object (Firestore doesn't accept undefined)
 */
export const removeUndefinedFields = (obj: any): any => {
  if (obj === null || typeof obj !== 'object') {
    return obj;
  }
  
  // Preserve Firestore Timestamp objects
  if (obj instanceof Timestamp) {
    return obj;
  }
  
  // Preserve objects that look like Firestore Timestamps (have seconds and nanoseconds)
  if (typeof obj === 'object' && 'seconds' in obj && 'nanoseconds' in obj) {
    return obj;
  }
  
  if (Array.isArray(obj)) {
    return obj.map(removeUndefinedFields);
  }
  
  const cleaned: any = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value !== undefined) {
      cleaned[key] = removeUndefinedFields(value);
    }
  }
  
  return cleaned;
};

/**
 * Sanitize filename for storage
 */
export const sanitizeFilename = (filename: string): string => {
  return filename.replace(/[^a-zA-Z0-9._-]/g, '_').toLowerCase();
};

/**
 * Get checklist template for a company
 * First checks if there's a company-specific template, 
 * otherwise returns the default template
 */
export const getChecklistTemplate = async (companyId: string): Promise<ChecklistTemplate | null> => {
  try {
    // First, try to get company-specific template
    const companyTemplateQuery = query(
      collection(db, 'checklistTemplates'),
      where('companyId', '==', companyId),
      where('isActive', '==', true)
    );
    
    const companySnapshot = await getDocs(companyTemplateQuery);
    
    if (!companySnapshot.empty) {
      const doc = companySnapshot.docs[0];
      return {
        id: doc.id,
        ...doc.data()
      } as ChecklistTemplate;
    }
    
    // If no company-specific template found, get the default template
    const defaultDocRef = doc(db, 'checklistTemplates', 'default_predeparture_checklist');
    const defaultSnapshot = await getDoc(defaultDocRef);
    
    if (defaultSnapshot.exists()) {
      return {
        id: defaultSnapshot.id,
        ...defaultSnapshot.data()
      } as ChecklistTemplate;
    }
    
    // No template found
    return null;
  } catch (error) {
    console.error('Error getting checklist template:', error);
    throw new Error('Failed to retrieve checklist template');
  }
};

/**
 * Get all active checklist templates
 */
export const getAllChecklistTemplates = async (): Promise<ChecklistTemplate[]> => {
  try {
    const templatesQuery = query(
      collection(db, 'checklistTemplates'),
      where('isActive', '==', true)
    );
    
    const snapshot = await getDocs(templatesQuery);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as ChecklistTemplate[];
  } catch (error) {
    console.error('Error getting all checklist templates:', error);
    throw new Error('Failed to retrieve checklist templates');
  }
};

/**
 * Search vehicles by plate number
 * Supports substring and case-insensitive search
 */
export const searchVehiclesByPlateNo = async (plateNo: string, companyId?: string): Promise<Vehicle[]> => {
  try {
    // Trim and validate search input
    const searchTerm = plateNo.trim();
    if (!searchTerm) {
      return []; // Return empty array for empty search
    }

    // Build query constraints dynamically
    const constraints = [
      // where('plateNo', '>=', searchTerm),
      // where('plateNo', '<=', searchTerm + '\uf8ff'),
      where('active', '==', true)
    ];
    if (companyId) {
      constraints.push(where('companyId', '==', companyId));
    }

    const vehiclesQuery = query(collection(db, 'vehicles'), ...constraints);
    const snapshot = await getDocs(vehiclesQuery);
    const allVehicles = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as Vehicle[];

    // Filter by plate number (case-insensitive partial match)
    const filteredVehicles = allVehicles.filter(vehicle =>
      vehicle.plateNo.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return filteredVehicles;
  } catch (error) {
    console.error('Error searching vehicles:', error);
    throw new Error('Failed to search vehicles');
  }
};

/**
 * Get vehicle by ID
 */
export const getVehicleById = async (vehicleId: string): Promise<Vehicle | null> => {
  try {
    const vehicleDoc = doc(db, 'vehicles', vehicleId);
    const snapshot = await getDoc(vehicleDoc);
    
    if (snapshot.exists()) {
      return {
        id: snapshot.id,
        ...snapshot.data()
      } as Vehicle;
    }
    
    return null;
  } catch (error) {
    console.error('Error getting vehicle:', error);
    throw new Error('Failed to retrieve vehicle');
  }
};

/**
 * Check if vehicle registration is expired
 */
export const isRegistrationExpired = (lastRegistration: Timestamp): boolean => {
  const registrationDate = lastRegistration.toDate();
  const currentDate = new Date();
  return registrationDate < currentDate;
};

/**
 * Get formatted registration status
 */
export const getRegistrationStatus = (registrationToExpire: any): {
  isExpired: boolean;
  daysUntilExpiry: number;
  statusText: string;
} => {
  if (!registrationToExpire) return {
    isExpired: false,
    daysUntilExpiry: 0,
    statusText: 'No registration date'
  };

  let registrationDateObj: Date | null = null;

  if (registrationToExpire instanceof Timestamp) {
    registrationDateObj = registrationToExpire.toDate();
  } else if (
    typeof registrationToExpire === 'object' &&
    registrationToExpire !== null &&
    'seconds' in registrationToExpire &&
    'nanoseconds' in registrationToExpire
  ) {
    registrationDateObj = new Timestamp(
      registrationToExpire.seconds,
      registrationToExpire.nanoseconds
    ).toDate();
  } else if (typeof registrationToExpire === 'string') {
    registrationDateObj = new Date(registrationToExpire);
  }

  if (!registrationDateObj || isNaN(registrationDateObj.getTime())) {
    return {
      isExpired: false,
      daysUntilExpiry: 0,
      statusText: 'Invalid registration date'
    };
  }

  const registrationDate = registrationDateObj;
  const currentDate = new Date();
  const diffTime = registrationDate.getTime() - currentDate.getTime();
  const daysUntilExpiry = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  const isExpired = daysUntilExpiry < 0;
  
  let statusText = '';
  if (isExpired) {
    statusText = `Expired ${Math.abs(daysUntilExpiry)} days ago`;
  } else if (daysUntilExpiry <= 30) {
    statusText = `Expires in ${daysUntilExpiry} days`;
  } else {
    statusText = `Valid until ${registrationDate.toLocaleDateString()}`;
  }
  
  return {
    isExpired,
    daysUntilExpiry,
    statusText
  };
};

// Function to get checklists by company and status
export const getChecklistsByCompanyAndStatus = async (
  companyId: string, 
  status: 'ongoing' | 'completed'
) => {
  try {
    const checklistsRef = collection(db, 'preDepartureChecklist');
    const q = query(
      checklistsRef,
      where('companyId', '==', companyId),
      where('status', '==', status)
    );
    
    const querySnapshot = await getDocs(q);
    const checklists: any[] = [];
    
    querySnapshot.forEach((doc) => {
      checklists.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return checklists;
  } catch (error) {
    console.error('Error fetching checklists:', error);
    throw error;
  }
};

// Function to get checklists by company and optional date range
export const getChecklistsByCompanyAndDate = async (
  companyId: string,
  createdFrom?: string,
  createdTo?: string
) => {
  try {
    const checklistsRef = collection(db, 'preDepartureChecklist');
    
    // Build query constraints
    const queryConstraints: any[] = [
      where('companyId', '==', companyId),
      where('status', 'in', ['completed', 'ongoing'])
    ];

    // Add date filtering if dates are provided
    if (createdFrom) {
      const fromDate = new Date(createdFrom);
      fromDate.setHours(0, 0, 0, 0); // Start of day
      queryConstraints.push(where('created_at', '>=', Timestamp.fromDate(fromDate)));
    }

    if (createdTo) {
      const toDate = new Date(createdTo);
      toDate.setHours(23, 59, 59, 999); // End of day
      queryConstraints.push(where('created_at', '<=', Timestamp.fromDate(toDate)));
    }

    // Add ordering by created_at descending
    queryConstraints.push(orderBy('created_at', 'desc'));
    
    // Execute query
    const q = query(checklistsRef, ...queryConstraints);
    const querySnapshot = await getDocs(q);
    const checklists: any[] = [];
    
    querySnapshot.forEach((doc) => {
      checklists.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return checklists;
  } catch (error) {
    console.error('Error fetching checklists by date:', error);
    throw error;
  }
};

// Interface for PDF generation parameters
export interface PDFGenerationParams {
  plateNumber: string;
  companyName: string;
  categories: ChecklistCategory[];
  totalRequired: number;
  completedRequired: number;
  isFullyComplete: boolean;
  overallStatus?: 'pass' | 'fail';
  inspectionSummary?: string;
  signatureDataURL?: string;
  registrationDate?: string;
  updated_at?: Timestamp;
  inspectorName?: string;
  checklistType?: string;
}

// Function to generate PDF summary for checklists
export const generatePDFSummary = async (params: PDFGenerationParams): Promise<string> => {
  const {
    plateNumber,
    companyName,
    categories,
    totalRequired,
    completedRequired,
    isFullyComplete,
    overallStatus,
    inspectionSummary,
    signatureDataURL,
    registrationDate,
    updated_at,
    checklistType = 'Pre-Departure Checklist'
  } = params;
  console.log('generatePDFSummary=>',{params})
  try {
    // Dynamically import jsPDF
    const jsPDF = (await import('jspdf')).default;
    
    // Create new PDF document
    const pdf = new jsPDF();
    let yPosition = 20;
    
    // Header
    pdf.setFontSize(20);
    pdf.setFont('helvetica', 'bold');
    pdf.text(checklistType, 105, yPosition, { align: 'center' });
    yPosition += 15;
    
    // Company and vehicle info
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'normal');
    pdf.text(`Company: ${companyName}`, 20, yPosition);
    yPosition += 8;
    pdf.text(`Plate Number: ${plateNumber}`, 20, yPosition);
    yPosition += 8;
    if (registrationDate) {
      pdf.text(`Registration: ${formatDateTimeDisplay(registrationDate)}`, 20, yPosition);
      yPosition += 8;
    }
    pdf.text(`Waybill Number: _________________ (To be filled by admin)`, 20, yPosition);
    yPosition += 8;
    pdf.text(`Date/Time inspected: ${formatDateTimeDisplay(updated_at)}`, 20, yPosition);
    yPosition += 8;
    // pdf.text(`Time: ${new Date().toLocaleTimeString()}`, 20, yPosition);
    // yPosition += 15;
    
    // Line separator
    pdf.line(20, yPosition, 190, yPosition);
    yPosition += 10;
    
    // Category sections
    for (const category of categories) {
      // Check if we need a new page
      if (yPosition > 250) {
        pdf.addPage();
        yPosition = 20;
      }
      
      // Category header
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text(`${category.name}`, 20, yPosition);
      yPosition += 10;
      
      // Category items
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      
      for (const item of category.items) {
        // Check if we need a new page for item
        if (yPosition > 270) {
          pdf.addPage();
          yPosition = 20;
        }
        
        // Item text with required indicator
        const itemText = item.required ? `${item.text} (*)` : item.text;
        pdf.text(`• ${itemText}`, 25, yPosition);
        
        // Status
        if (item.status) {
          const statusText = item.status === 'green' ? 'GOOD' : 
                           item.status === 'yellow' ? 'CAUTION' : 'BAD';
          pdf.text(`Status: ${statusText}`, 130, yPosition);
        }
        
        yPosition += 6;
        
        // Comment if exists
        if (item.comment && item.comment.trim()) {
          const commentLines = pdf.splitTextToSize(`Comment: ${item.comment}`, 160);
          pdf.text(commentLines, 30, yPosition);
          yPosition += commentLines.length * 5;
        }
        
        // Photo - Show text indication only (skip image loading to avoid CORS issues)
        if (item.photo) {
          pdf.setFontSize(8);
          pdf.text('Photo: Evidence captured (images not shown in PDF)', 30, yPosition);
          yPosition += 8;
          pdf.setFontSize(10);
        }
        
        yPosition += 3; // Space between items
      }
      
      yPosition += 8; // Space between categories
    }
    
    // Summary section
    if (yPosition > 180) {
      pdf.addPage();
      yPosition = 20;
    }
    
    yPosition += 10;
    pdf.line(20, yPosition, 190, yPosition);
    yPosition += 10;
    
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Summary', 20, yPosition);
    yPosition += 10;
    
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    pdf.text(`Total Required Items: ${totalRequired}`, 20, yPosition);
    yPosition += 6;
    pdf.text(`Completed Required Items: ${completedRequired}`, 20, yPosition);
    yPosition += 6;
    pdf.text(`Completion Status: ${isFullyComplete ? 'COMPLETE' : 'INCOMPLETE'}`, 20, yPosition);
    yPosition += 15;
    
    // Final Assessment Section
    if (overallStatus && inspectionSummary) {
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Final Assessment', 20, yPosition);
      yPosition += 10;
      
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      
      // Overall Status
      const statusText = overallStatus === 'pass' ? '✅ PASS' : '❌ FAIL';
      const statusColor = overallStatus === 'pass' ? [0, 128, 0] : [255, 0, 0]; // Green or Red
      pdf.setTextColor(statusColor[0], statusColor[1], statusColor[2]);
      pdf.text(`Overall Status: ${statusText}`, 20, yPosition);
      pdf.setTextColor(0, 0, 0); // Reset to black
      yPosition += 10;
      
      // Inspection Summary
      pdf.setFont('helvetica', 'bold');
      pdf.text('Inspector Summary:', 20, yPosition);
      yPosition += 6;
      
      pdf.setFont('helvetica', 'normal');
      const summaryLines = pdf.splitTextToSize(inspectionSummary, 170);
      pdf.text(summaryLines, 20, yPosition);
      yPosition += summaryLines.length * 5 + 10;
    }
    
    // Inspector signature section
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    
    if (signatureDataURL) {
      // Add digital signature to PDF
      try {
        // Add signature image to PDF
        pdf.text('Inspector Signature:', 20, yPosition);
        yPosition += 8;
        
        // Add the signature image (convert base64 to proper format for PDF)
        const imgWidth = 60; // Width in mm
        const imgHeight = 20; // Height in mm
        pdf.addImage(signatureDataURL, 'PNG', 20, yPosition, imgWidth, imgHeight);
        yPosition += imgHeight + 5;

        pdf.text(`Signed on: ${formatDateTimeDisplay(updated_at)}`, 20, yPosition);
        yPosition += 8;
      } catch (error) {
        console.error('Error adding signature to PDF:', error);
        // Fallback to text line if signature fails
        pdf.text('Inspector: ________________________________', 20, yPosition);
        yPosition += 8;
        pdf.text(`Date/Time: ${formatDateTimeDisplay(updated_at)}`, 20, yPosition);
        yPosition += 8;
      }
    } else {
      // Fallback to signature line if no signature
      pdf.text('Inspector: ________________________________', 20, yPosition);
      yPosition += 8;
      pdf.text(`Date/Time: ${formatDateTimeDisplay(updated_at)}`, 20, yPosition);
      yPosition += 8;
    }
    
    yPosition += 7;
    
    // Required items note
    pdf.setFontSize(8);
    pdf.text('(*) Required items', 20, yPosition);
    
    // Generate filename and save
    const fileName = `${checklistType.replace(/\s+/g, '_')}_${plateNumber.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
    pdf.save(fileName);

    return fileName;
  } catch (error) {
    console.error('Error generating PDF:', error);
    throw error;
  }
};

/**
 * Get checklist by ID - wrapper function for easier reuse
 * This is an alias for getChecklistFromFirestore for consistency
 */
export const getChecklistById = async (checklistId: string): Promise<PreDepartureChecklistData | null> => {
  return await getChecklistFromFirestore(checklistId);
};
